<script setup lang="ts">
import { TaskParameterModel, TaskParameterTypeEnum, TaskParameter } from "@/components";
import { computed, onBeforeMount, PropType, ref, watch } from "vue";
import {
  TirPmRadio,
  TirPmScrollbar,
  TirPmDraggableSorting,
  DragModel,
  TirPmOptionListItemElement,
  TirPmInput,
  TirPmButton,
  TirPmButtonVariantEnum,
  TirPmButtonSizeEnum,
} from "tir-components";
import {
  TrashIcon,
  PencilSquareIcon,
  CrossCompactIcon,
  CheckIcon,
} from "tir-style-system/icons/outline";
import { TaskStatusDropdownNew } from "@/components/task-forms/components";
import { StatusModel } from "@/components/task-forms/models";
import { TaskStatusesServiceAdapter } from "../adapter-service";
import TirPmGlobalStatus from "./TirPmGlobalStatus.vue";
import { ParameterRemoveModal } from "./";

/**Параметры */
const props = defineProps({
  /**Список параметров */
  parameters: {
    type: Array as PropType<TaskParameterModel>,
    default: () => [] as TaskParameterModel[],
  },
  /**Тип настройки */
  type: { type: Number as PropType<TaskParameterTypeEnum> },
  /**Заголовок параметра */
  title: { type: String, default: "" },
  /**Имя области */
  namespaceName: { type: String },
});
/**События */
const emit = defineEmits<{
  /**Удаление параметра */
  remove: [name: string];
}>();

const adapter = new TaskStatusesServiceAdapter();
/**Стандартный параметр */
const defaultParameterIndex = ref<number | string>();
/**Индекс наведенного элемента */
const hoverIndex = ref<number>();
/**Сфокусирован ли какой то параметр */
const isFocus = ref();
const isOpened = ref(false);
const statuses = ref([]);
/**Окно для подтверждения удаления параметра */
const $parameterRemoveModal = ref<InstanceType<typeof ParameterRemoveModal>>();
/**Сфокусированный параметр */
const focusedParameter = ref<string | number>();
/**Элемент таблицы */
const $table = ref<HTMLElement>();
/**Вводимое описание */
const description = ref("");

/**Параметр рабочей области */
const isNamespaceParameter = computed(
  () => props.type == TaskParameterTypeEnum.Type || props.type == TaskParameterTypeEnum.Difficulty,
);
/**Базовый параметр */
const basicParameter = computed(() => props.parameters.find((p) => p.IsDefault));
/**Показывать ли настройку глобального статуса */
const showGlobalStatus = computed(() => props.type == TaskParameterTypeEnum.Status);
/**Перетаскиваемые параметры */
const dragParameters = computed(() =>
  props.parameters.map(
    (x) =>
      new DragModel({
        AllowDrag: true,
        AllowDrop: true,
        Data: x,
      }),
  ),
);
/**Показывать ли поле ввода описания */
const showDescription = computed(() => {
  return (
    props.type == TaskParameterTypeEnum.Type ||
    props.type == TaskParameterTypeEnum.Status ||
    props.type == TaskParameterTypeEnum.Tag
  );
});
/**Имя параметра */
const parameterName = computed(() => {
  switch (props.type) {
    case TaskParameterTypeEnum.Status:
      return "Статус";
    case TaskParameterTypeEnum.Priority:
      return "Приоритет";
    case TaskParameterTypeEnum.Tag:
      return "Тег";
    case TaskParameterTypeEnum.Type:
      return "Тип";
    case TaskParameterTypeEnum.Difficulty:
      return "Сложность";
  }
});

/**Отслеживание изменений параметров */
watch(() => props.parameters, onUpdateParameters, { deep: true });

/**Перед рендером */
onBeforeMount(async () => {
  onUpdateParameters();
  await getStatuses();
});

/**При изменении параметров */
function onUpdateParameters() {
  const defaultParameter = props.parameters.findIndex((x) => x.IsDefault);
  defaultParameterIndex.value = defaultParameter;
}
/**Смена стандартного параметра */
const changeDefault = (_index: number) => {
  props.parameters.forEach((x, index) => (x.IsDefault = index == _index));
};
/**Получение класса элемента */
const getItemClass = (index: number) => {
  return { hover: hoverIndex.value == index };
};
/**Установка значения поля hoverIndex */
const setHoverIndex = (index?: number) => {
  if (!isFocus.value) hoverIndex.value = index;
};
const isOpenedCustomItem = ref(false);
/**Удалить параметр */
const removeParameter = (name: string) => {
  const removeIndex = props.parameters.findIndex((x) => x.Name == name);
  if (removeIndex != -1) {
    setTimeout(() => {
      hoverIndex.value = undefined;
      props.parameters.splice(removeIndex, 1);
      if (!props?.parameters || props?.parameters?.length == 0) {
        return;
      }
      if (props.parameters.reduce((acc, x) => acc + (x.IsDefault ? 1 : 0), 0) == 0) {
        props.parameters[0].IsDefault = true;
      }
    });
  }
};

/**Обновить сортировку параметров */
const sortParameters = (_parameters: DragModel<TaskParameterModel>[]) => {
  props.parameters.splice(0, props.parameters.length);
  props.parameters.push(..._parameters.map((x) => x.Data));
};

async function getStatuses() {
  const res = await adapter.getGlobalStatusList();
  statuses.value = res.Value.map(
    (x) =>
      new StatusModel({ Color: x.Color, Id: x.Id as any, Name: x.Name, IsDefault: x.IsDefault }),
  );
}
const selGlobalItem = ref();
function onChangeStatus(item: StatusModel, slot: TaskParameterModel) {
  slot.OrganizationGlobalStatus = new TaskParameterModel({
    Id: item.Id,
    Name: item.Name,
    Color: item.Color,
  });
  selGlobalItem.value = item;
  isOpenedCustomItem.value = null;
  isOpened.value = false;
}
function onIsOpened(value: any) {
  isOpenedCustomItem.value = value;
}
/**Клик по кнопке удаления */
const onClickRemove = (parameter: TaskParameterModel) => {
  if (parameter.InUse && isNamespaceParameter.value) {
    $parameterRemoveModal.value?.open(parameter, basicParameter.value);
  } else {
    removeParameter(parameter.Name);
  }
};
/**Проверка возможности удаления */
const checkAllowRemove = (parameter: TaskParameterModel) => {
  return (
    !parameter.IsDefault &&
    props.parameters.length > 1 &&
    (!parameter.InUse || isNamespaceParameter.value)
  );
};
/**Проверка отображения поля ввода описания */
const checkShowDescriptionInput = (parameter: TaskParameterModel) => {
  return parameter.Id && focusedParameter.value == parameter.Id;
};
/**Старт добавления описания */
const addDescription = (parameter: TaskParameterModel) => {
  description.value = parameter.Description;
  focusedParameter.value = parameter.Id;
  const parameterIndex = props.parameters.findIndex((p) => p.Id == parameter.Id);
  const inputs = $table.value?.querySelectorAll(".task-parameters-table_item_description_input");

  const input = inputs?.[parameterIndex]?.querySelector(
    ".input-container__input",
  ) as HTMLInputElement;
  if (input) setTimeout(() => input.click());
};
/**Сброс фокуса с поля ввода описания */
const blurDescription = () => {
  focusedParameter.value = undefined;
  description.value = "";
};
/**Подтверждения изменения описания */
const confirmDescription = (parameter: TaskParameterModel) => {
  parameter.Description = description.value;
  setTimeout(() => blurDescription());
};
</script>
<template>
  <div class="task-parameters-table" ref="$table">
    <div class="task-parameters-table_item is-header border-bottom">
      <div v-text="parameterName" class="task-parameters-table_item_parameter" />
      <div class="task-parameters-table_item_divider" :class="{ hidden: !showGlobalStatus }" />
      <template v-if="!showDescription || showGlobalStatus">
        <div class="task-parameters-table_item_name">
          <span v-if="showGlobalStatus">Глобальные статусы</span>
        </div>
      </template>
      <div class="task-parameters-table_item_divider" />
      <template v-if="showDescription">
        <div
          class="task-parameters-table_item_description"
          :class="{ 'with-status': showGlobalStatus }"
        >
          Описание
        </div>
        <div class="task-parameters-table_item_divider" />
      </template>
      <div class="task-parameters-table_item_buttons">Действия</div>
      <div class="task-parameters-table_item_divider" />
      <div class="task-parameters-table_item_default">{{ title }} по умолчанию</div>
    </div>
    <TirPmScrollbar show-on-hover>
      <div class="task-parameters-table_list">
        <TirPmDraggableSorting
          is-allow-click-delay
          dataClass="task-parameters-table_drag-item"
          :data="dragParameters"
          :indent="0"
          #item="slot"
          @change="sortParameters"
        >
          <div
            class="task-parameters-table_item"
            :class="getItemClass(slot.index)"
            :key="slot.item.Id || slot.item.TempId"
            @mousemove="setHoverIndex(slot.index)"
            @mouseleave="setHoverIndex(undefined)"
          >
            <div class="task-parameters-table_item_parameter">
              <TaskParameter :data="slot.item" :type="type" hide-remove />
            </div>
            <div
              class="task-parameters-table_item_name"
              v-if="!showDescription || showGlobalStatus"
            >
              <TaskStatusDropdownNew
                @isOpened="onIsOpened(slot.item)"
                v-if="
                  showGlobalStatus &&
                  ((hoverIndex == slot.index && !isOpenedCustomItem) ||
                    isOpenedCustomItem == slot.item ||
                    (slot.item.OrganizationGlobalStatus && hoverIndex != slot.index))
                "
                :isOpened="isOpened"
                :selected="slot.item.OrganizationGlobalStatus"
              >
                <template #content>
                  <TirPmOptionListItemElement
                    is-hoverable
                    v-for="(item, idx) of statuses"
                    @click="onChangeStatus(item, slot.item)"
                    :key="idx"
                  >
                    <TirPmRadio
                      v-model="defaultParameterIndex"
                      :value="((slot?.item?.OrganizationGlobalStatus?.Id==item?.Id) && !!slot?.item?.OrganizationGlobalStatus?.Id) as any"
                    />
                    <TirPmGlobalStatus :value="item" />
                  </TirPmOptionListItemElement>
                </template>
              </TaskStatusDropdownNew>
            </div>
            <div
              v-if="showDescription"
              class="task-parameters-table_item_description"
              :class="{ 'with-status': showGlobalStatus }"
            >
              <div v-show="checkShowDescriptionInput(slot.item)">
                <TirPmInput
                  v-model="description"
                  is-micro
                  class="task-parameters-table_item_description_input"
                  :is-with-hint="false"
                  @enter="confirmDescription(slot.item)"
                  @blur="blurDescription"
                />
                <div class="task-parameters-table_item_description_buttons">
                  <CheckIcon @mousedown="confirmDescription(slot.item)" />
                  <CrossCompactIcon @mousedown="blurDescription" />
                </div>
              </div>
              <span
                v-if="slot.item.Description && focusedParameter != slot.item.Id"
                v-text="slot.item.Description"
                class="task-parameters-table_item_description_text"
              />
              <TirPmButton
                v-show="focusedParameter != slot.item.Id"
                v-model="description"
                class="task-parameters-table_item_description_button"
                :class="{ 'with-description': !!slot.item.Description }"
                :variant="TirPmButtonVariantEnum.LinkState"
                :size="TirPmButtonSizeEnum.Small"
                @click="addDescription(slot.item)"
              >
                <PencilSquareIcon class="task-parameters-table_item_description_icon" />
                <span v-if="!slot.item.Description">Добавить описание</span>
              </TirPmButton>
            </div>
            <div class="task-parameters-table_item_buttons">
              <div class="task-parameters-table_item_buttons_wrapper" />
              <div
                v-if="hoverIndex == slot.index"
                class="task-parameters-table_item_buttons_wrapper"
              >
                <div
                  v-if="checkAllowRemove(slot.item)"
                  class="task-parameters-table_item_icon"
                  @mousedown.stop
                  @click="onClickRemove(slot.item)"
                >
                  <TrashIcon />
                </div>
              </div>
            </div>
            <div class="task-parameters-table_item_default">
              <TirPmRadio
                v-model="defaultParameterIndex"
                :value="slot.index"
                @update:model-value="changeDefault(slot.index)"
              />
            </div>
          </div>
        </TirPmDraggableSorting>
      </div>
    </TirPmScrollbar>
  </div>
  <ParameterRemoveModal
    ref="$parameterRemoveModal"
    :namespaceName="namespaceName"
    @remove="removeParameter"
  />
</template>
<style lang="scss">
.task-parameters-table {
  display: flex;
  flex-direction: column;
  cursor: default;
  &_list {
    max-height: calc(100vh - 27rem);
  }
  &_item_name_button {
    width: 100% !important;
  }
  &_item {
    display: flex;
    flex-wrap: nowrap;
    align-items: center;
    height: 2rem;
    transition: 0.2s linear;
    padding-left: 0.5rem;
    border-radius: 0.375rem;
    background-color: var(--bg-base);

    &_name {
      justify-content: flex-start !important;
    }
    & > div {
      display: flex;
      align-items: center;
      height: 100%;
      color: var(--text-tertiary, #93969b);
      font-size: 0.6875rem;
    }

    &_divider {
      position: relative;
      height: 2rem;
      width: 0.75rem;
      &::after {
        content: "";
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        width: 0.0625rem;
        height: 1.5rem;
        background: var(--neutral-20);
      }

      &.hidden {
        &::after {
          display: none;
        }
      }
    }

    &.border-bottom {
      border-radius: 0.063rem;
      border-bottom: 1px solid var(--neutral-20, #d4d5d7);
    }

    &:not(.is-header) {
      height: 3.5rem;
      gap: 0.75rem;
      &.hover {
        background: var(--neutral-translucent-5, rgba(39, 45, 55, 0.05));
      }
    }
    &_parameter {
      min-width: 25%;
    }

    &_name {
      min-width: 22.6%;
      .input-container__input-wrapper {
        height: 2rem;
      }
      .input-container--small {
        height: 2rem;
      }
    }
    &_description {
      position: relative;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 0.875rem;
      gap: 0.25rem;
      overflow: hidden;
      text-overflow: ellipsis;
      width: 55%;
      &.with-status {
        width: 22.6%;
      }
      & > div {
        width: 100%;
      }
      &_button.button--small {
        min-width: fit-content;
        padding: 0 0.25rem;
        .button__sign-main {
          display: flex;
          align-items: center;
          gap: 0.25rem;
        }
      }
      &_button:not(.with-description),
      &_input {
        width: 100% !important;
      }
      &_input input {
        padding-right: 3.5rem;
      }
      &_icon {
        width: 1rem;
        height: 1rem;
      }
      &_buttons {
        position: absolute;
        top: 50%;
        right: 0.5rem;
        display: flex;
        justify-content: center;
        align-items: center;
        gap: 0.25rem;
        transform: translateY(-50%);
        z-index: 1;
        svg {
          width: 1.5rem;
          height: 1.5rem;
          cursor: pointer;
          color: var(--text-primary);
        }
      }
      &_text {
        max-width: 100%;
        max-height: 100%;
        word-break: break-word;
        color: var(--text-primary);
        font-size: 0.875rem;
        padding: 0.5rem 0;
      }
    }
    &_buttons {
      min-width: 10%;
      &_wrapper {
        display: flex;
        gap: 0.5rem;
      }
    }
    &_default {
      min-width: 10%;
    }
    &_name,
    &_buttons,
    &_default {
      justify-content: center !important;
    }

    &_icon {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 2rem;
      height: 2rem;
      cursor: pointer;
      border-radius: 0.25rem;
      transition: 0.1s ease;
      color: var(--text-primary);
      &:hover {
        background-color: var(--neutral-translucent-5);
      }
      & > svg {
        width: 1.25rem;
        height: 1.25rem;
      }
    }
  }
  .task-parameters-table_drag-item.dragging {
    background-color: var(--neutral-translucent-5);
    .task-parameters-table_item_default,
    .task-parameters-table_item_buttons,
    .task-parameters-table_item_name {
      display: none;
    }
  }
}
</style>
